// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Communication } from '@medplum/fhirtypes';
import { getAboutId, getNotificationKey } from './communication';
import type { NotificationAudience } from './types';

/**
 * Map a notification Communication to a deep-link href in the corresponding
 * app. Returns undefined when the notification key cannot be determined; falls
 * back to category-level routes (e.g. "/appointments") when the related
 * resource ID is missing.
 *
 * Patient routes target the patient app; provider routes target the provider
 * app. Callers should pass the audience that matches the running app.
 */
export function getNotificationHref(communication: Communication, audience: NotificationAudience): string | undefined {
  const key = getNotificationKey(communication);
  if (!key) {
    return undefined;
  }
  return audience === 'PROVIDER' ? getProviderHref(communication, key) : getPatientHref(communication, key);
}

function getPatientHref(c: Communication, key: string): string {
  if (key === 'patient.account.intake-questionnaire-reminder') {
    return '/?openIntakeQuestionnaire=true';
  }
  if (
    key.startsWith('patient.system.appointment-') ||
    key === 'patient.reminder.intake-appointment' ||
    key === 'patient.reminder.diagnostic-appointment' ||
    key === 'patient.reminder.followup-30' ||
    key === 'patient.reminder.followup-90' ||
    key === 'patient.reminder.followup-150'
  ) {
    return '/appointments';
  }
  if (key === 'patient.reminder.appointment-scheduling') {
    return '/appointments/schedule';
  }
  if (key === 'patient.system.new-provider-message' || key === 'patient.system.new-provider-note') {
    return '/messages';
  }
  if (key.startsWith('patient.troubleshooting.')) {
    return '/messages';
  }
  if (
    key.startsWith('patient.hsat.') ||
    key === 'patient.reminder.hsat' ||
    key === 'patient.reminder.hsat-final' ||
    key === 'patient.reminder.prescription-available' ||
    key === 'patient.dme.adherence-reminder'
  ) {
    return '/my-chart';
  }
  if (
    key.startsWith('patient.account.') ||
    key === 'patient.system.validate-account' ||
    key === 'patient.system.incomplete-information' ||
    key === 'patient.system.chart-update'
  ) {
    return '/my-chart';
  }
  if (key.startsWith('patient.payment.')) {
    let chargeItemId = getAboutId(c, 'ChargeItem');
    if (!chargeItemId && c.note?.[0]?.text) {
      try {
        const context = JSON.parse(c.note[0].text) as Record<string, unknown>;
        const ref = context.chargeItemId;
        if (typeof ref === 'string' && /^ChargeItem\/[A-Za-z0-9.-]+$/.test(ref)) {
          chargeItemId = ref.slice('ChargeItem/'.length);
        }
      } catch {
        // Older notification notes may not contain JSON context.
      }
    }
    if (key === 'patient.payment.new-payment' && chargeItemId) {
      return `/pay-charge/${encodeURIComponent(chargeItemId)}`;
    }
    return '/settings';
  }
  return '/notifications';
}

function getProviderHref(c: Communication, key: string): string {
  if (
    key === 'provider.system.appointment-created' ||
    key === 'provider.system.appointment-cancelled' ||
    key === 'provider.reminder.appointment'
  ) {
    const patientId = getAboutId(c, 'Patient');
    return patientId ? `/Patient/${patientId}/Appointment` : '/schedule';
  }
  if (key === 'provider.system.new-patient-message' || key === 'provider.system.new-provider-message') {
    return '/Communication?status=in-progress';
  }
  if (key === 'provider.system.new-patient-triage') {
    return '/triage';
  }
  if (key === 'provider.system.hsat-results-available') {
    const patientId = getAboutId(c, 'Patient');
    return patientId ? `/Patient/${patientId}/medical-info` : '/Patient';
  }
  if (key === 'provider.reminder.rpm-monthly') {
    const patientId = getAboutId(c, 'Patient');
    return patientId ? `/Patient/${patientId}/devices` : '/Patient';
  }
  return '/notifications';
}
